import React from 'react';
import { FaSearch } from 'react-icons/fa';

const AdminMappingModal = ({
  showAdminMappingModal,
  setShowAdminMappingModal,
  adminMappingData,
  setAdminMappingData,
  adminSearch,
  setAdminSearch,
  userSearch,
  setUserSearch,
  adminSuggestions,
  userSuggestions,
  selectedUsersForMapping,
  setSelectedUsersForMapping,
  loadingAdminMapping,
  handleAdminUserMapping,
  handleUserCheckboxForAdminMapping
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Map Users to Admin
        </h3>
        <div className="space-y-4">
          {/* Admin Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Admin *
            </label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search admins by email..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={adminSearch}
                onChange={(e) => setAdminSearch(e.target.value)}
              />
            </div>
            
            <div className="mt-2 border border-gray-300 dark:border-gray-600 rounded-lg max-h-40 overflow-y-auto">
              {adminSuggestions.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                  {adminSearch ? 'No admins found matching your search' : 'No admins available'}
                </div>
              ) : (
                adminSuggestions.map((admin) => (
                  <div
                    key={admin.id}
                    className={`flex items-center px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                      adminMappingData.admin_id === admin.id.toString() ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                    onClick={() => setAdminMappingData({
                      ...adminMappingData,
                      admin_id: admin.id.toString()
                    })}
                  >
                    <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white text-sm mr-3 ${
                      admin.role === 'superadmin' ? 'bg-purple-600' : 'bg-blue-600'
                    }`}>
                      {admin.email.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{admin.email}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {admin.role === 'superadmin' ? 'Super Admin' : 'Admin'} • Manages {admin.managed_users?.length || 0} users
                      </div>
                    </div>
                    {adminMappingData.admin_id === admin.id.toString() && (
                      <div className="text-green-500 text-lg">
                        ✓
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            
            {adminMappingData.admin_id && (
              <div className="mt-2">
                <span className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                  Selected Admin: {adminSuggestions.find(a => a.id.toString() === adminMappingData.admin_id)?.email}
                  <button
                    type="button"
                    className="ml-2 text-blue-600 hover:text-blue-800"
                    onClick={() => setAdminMappingData({
                      ...adminMappingData,
                      admin_id: ''
                    })}
                  >
                    ×
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* User Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Users to Map *
            </label>
            <div className="relative mb-2">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search users by email..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>
            
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto">
              {userSuggestions.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                  {userSearch ? 'No users found matching your search' : 'No users available'}
                </div>
              ) : (
                userSuggestions.map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      selectedUsersForMapping.includes(user.id) ? 'bg-green-50 dark:bg-green-900/20' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`user-${user.id}`}
                      checked={selectedUsersForMapping.includes(user.id)}
                      onChange={() => handleUserCheckboxForAdminMapping(user.id)}
                      className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                    <label 
                      htmlFor={`user-${user.id}`} 
                      className="ml-3 flex-1 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        handleUserCheckboxForAdminMapping(user.id);
                      }}
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white text-sm mr-3 bg-green-600">
                          {user.email.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{user.email}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">User ID: {user.id}</div>
                        </div>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
            
            {selectedUsersForMapping.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Selected: {selectedUsersForMapping.length} user(s)
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedUsersForMapping.map(userId => {
                    const user = userSuggestions.find(u => u.id === userId);
                    return user ? (
                      <span 
                        key={userId}
                        className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded"
                      >
                        {user.email}
                        <button
                          type="button"
                          className="ml-1 text-green-600 hover:text-green-800"
                          onClick={() => handleUserCheckboxForAdminMapping(userId)}
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => {
              setShowAdminMappingModal(false);
              setAdminMappingData({
                admin_id: '',
                user_ids: []
              });
              setSelectedUsersForMapping([]);
            }}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={loadingAdminMapping}
          >
            Cancel
          </button>
          <button
            onClick={handleAdminUserMapping}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loadingAdminMapping || !adminMappingData.admin_id || adminMappingData.user_ids.length === 0}
          >
            {loadingAdminMapping ? (
              <>
                <span className="inline-block animate-spin mr-2">⟳</span>
                Mapping...
              </>
            ) : (
              'Map Users to Admin'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMappingModal;